'use client';

import { ChangeEvent, useEffect, useState } from 'react';
import { cartExecuteOttPayment } from 'actions/cart';
import config from 'config';
import {
  BANK_ICONS,
  creditCardMask,
  creditCardSchema,
  getApiError,
  months,
  securityCodeMask,
  zipCodeMask
} from 'constant';
import { FormikValues, useFormik } from 'formik';
import Image from 'next/image';
import { useGetStatesQuery } from 'services/locationsApi';
import { useAddedPaymentMethodMutation } from 'services/paymentMethodsApi';

import {
  Box,
  Button,
  Checkbox,
  Flex,
  Stack,
  useBreakpointValue,
  useTheme
} from '@chakra-ui/react';

import { useAppDispatch, useAppSelector } from 'store/hooks';
import { setError } from 'store/reducers/giftCardsSlice';
import { updateGuestZip } from 'store/reducers/persistGuestAuthSlice';

import {
  formatCurrency,
  getCardType,
  getYearOptions,
  removeSpaces,
  transformToReactSelectOptions
} from 'utils';
import { RawFormData } from 'utils/cayanFormHelper';

import { CCardBase, CCardInterface, ScreenTypeEnum } from 'types';

import { ErrorModal } from 'components/PaymentList/components';
import { Loader, NoWrap, SelectField, Text, TextField } from 'ui';

const {
  payment: { title, description }
} = config;

const initialValues: FormikValues = {
  card_number: '',
  card_cvv2: '',
  exp_month: '',
  exp_year: '',
  owner: '',
  addr_ln1: '',
  city: '',
  state: '',
  zip: ''
};

interface NewCardFormProps {
  onScreenTypeChange: (type: ScreenTypeEnum) => void;
  isAlternativeBehavior?: boolean;
  onBack?: () => void;
  errorMsg?: string;
  selectCCardId?: (id: number) => void;
  isGuestFlow?: boolean;
  onPayment?: (OTTtoken: string) => Promise<void | boolean>;
  enableOttFlow?: boolean;
  onOttSuccess?: () => void;
  paymentAmount?: number;
}

const NewCardForm = ({
  onScreenTypeChange,
  isAlternativeBehavior = false,
  onBack = () => {},
  selectCCardId = () => {},
  isGuestFlow = false,
  errorMsg = '',
  onPayment = async (_: string) => {},
  enableOttFlow = false,
  onOttSuccess = () => {},
  paymentAmount = 0
}: NewCardFormProps) => {
  const dispatch = useAppDispatch();
  const { userInfo } = useAppSelector((state) => state.persistAuth);
  const { guestInfo } = useAppSelector((state) => state.persistGuestAuth);
  const { data: states = [], isLoading: isStatesLoading } =
    useGetStatesQuery('') ?? {};
  const [
    addCreditCard,
    { isLoading, error: cardErrorMsg = '', isError, reset: clearErrors }
  ] = useAddedPaymentMethodMutation();
  const [shouldSaveCard, setShouldSaveCard] = useState(false);

  const {
    colors: {
      palette: {
        components: { newCardForm }
      }
    }
  } = useTheme();
  const inputSize: 'lg' | 'xl' | undefined = useBreakpointValue(
    { base: 'lg', lg: 'xl' },
    { fallback: 'xl' }
  );
  const isMobile = useBreakpointValue(
    { base: true, md: false },
    { fallback: 'md' }
  );

  const handleOttPurchase = async (formData: FormikValues) => {
    const ottSuccess = await dispatch(
      cartExecuteOttPayment(formData as RawFormData, onPayment)
    );
    if (ottSuccess) {
      onOttSuccess();
    }
  };

  const {
    handleSubmit,
    getFieldProps,
    resetForm,
    validateField,
    setFieldValue,
    setFieldTouched,
    errors,
    touched,
    values,
    dirty,
    isValid
  } = useFormik({
    initialValues,
    validationSchema: creditCardSchema,
    validateOnBlur: true,
    validateOnChange: true,
    onSubmit: async (formData: FormikValues) => {
      if (userInfo || guestInfo) {
        if (isGuestFlow || (enableOttFlow && !shouldSaveCard)) {
          await handleOttPurchase(formData);
          return;
        }

        if (userInfo) {
          const cardData: CCardInterface = {
            ...(formData as CCardBase),
            card_number: removeSpaces(formData.card_number),
            allow_billing: 2,
            exp_month: +formData.exp_month,
            exp_year: +formData.exp_year,
            clid: userInfo.clid,
            token: userInfo.token
          };
          const card = await addCreditCard(cardData).unwrap();
          if (card?.id) {
            onScreenTypeChange(ScreenTypeEnum.cardList);
            selectCCardId(card?.id);
          }
        }
      }
    }
  });

  const yearsOptions = getYearOptions();
  const formattedStates = transformToReactSelectOptions(states);
  const bank = getCardType(values.card_number);
  const bankIcon = bank ? BANK_ICONS[bank] : '';
  const isSubmitDisabled = !dirty || !isValid || isLoading || isStatesLoading;
  const APIError = cardErrorMsg || errorMsg;

  const handleClearError =
    (withReset: boolean = false) =>
    () => {
      clearErrors();
      dispatch(setError(''));

      if (withReset) {
        const resetValues = {
          ...initialValues,
          exp_month: undefined,
          exp_year: undefined,
          state: undefined
        };
        resetForm({ values: resetValues });
      }
    };

  const getError = (fieldName: string) => errors[fieldName];

  const handleCancel = () => {
    onScreenTypeChange(ScreenTypeEnum.cardList);
  };

  const resetFieldHandler = (field: string) => () => {
    setFieldValue(field, initialValues[field]);
  };

  const handleSelectChange = async (name: string, value: string | number) => {
    await setFieldValue(name, value);
    if (!isValid || isError) {
      handleClearError()();
    }
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const {
      target: { name, value }
    } = event;
    setFieldValue(name, value);
    if (!isValid || isError) {
      handleClearError()();
    }
  };

  const handleSaveCardChange = (e: ChangeEvent<HTMLInputElement>) => {
    setShouldSaveCard(e.target.checked);
  };

  const getButtonText = () => {
    if (isGuestFlow) {
      return `Pay ${formatCurrency(paymentAmount)} Now`;
    }
    if (enableOttFlow && !shouldSaveCard) {
      return `Pay ${formatCurrency(paymentAmount)} Now`;
    }
    return 'Save';
  };

  useEffect(
    () => () => {
      handleClearError()();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useEffect(() => {
    if (isGuestFlow && values.zip) {
      dispatch(updateGuestZip(values.zip));
    }
  }, [dispatch, isGuestFlow, values.zip]);

  return (
    <Box position='relative' as='form' onSubmit={handleSubmit}>
      <Stack
        borderRadius={6}
        px={['15px', '15px', '25px', '50px']}
        py={['25px', '30px']}
        bg={newCardForm.bg}
      >
        <Flex
          alignItems='center'
          justifyContent='space-between'
          gap={{ lg: '15px', xl: '25px' }}
          w='100%'
          mb={{ base: '20px', md: '30px' }}
        >
          <TextField
            autoFocus
            {...getFieldProps('card_number')}
            error={getApiError('card_number', errorMsg as string)}
            touched={!!touched.card_number}
            onReset={resetFieldHandler('card_number')}
            label='Credit Card Number'
            isFieldValue={!!values.card_number}
            onChange={handleInputChange}
            size={inputSize}
            width={{ base: '100%', lg: '80%' }}
            containerStyles={{
              '& label': { fontSize: { base: '14px', lg: '15px' } }
            }}
            mask={creditCardMask}
            fontSize={{
              base: '22px',
              lg: '40px',
              xl: '50px'
            }}
          />

          {bankIcon && (
            <Flex
              display={{ base: 'none', lg: 'flex' }}
              width={{ base: '0%', lg: '20%' }}
              justifyContent='flex-end'
            >
              <Box
                width='160px'
                height='117px'
                justifyContent='center'
                mt='20px'
                position='relative'
                sx={{
                  img: {
                    width: 'auto !important'
                  }
                }}
              >
                <Image src={bankIcon} alt='Credit card' fill sizes='100px' />
              </Box>
            </Flex>
          )}
        </Flex>

        <Flex flexWrap='wrap' flexDirection={{ base: 'column', lg: 'row' }}>
          <Flex
            w={{ base: '100%', lg: '50%' }}
            pr={{ base: '0', lg: '20px', xl: '30px' }}
            pb={{ base: '30px', lg: '0' }}
            borderRight={{
              base: 'none',
              lg: `1px solid ${newCardForm.borderColor}`
            }}
            borderBottom={{
              base: `1px solid ${newCardForm.borderColor}`,
              lg: 'none'
            }}
            direction='column'
            gap='15px'
          >
            <TextField
              {...getFieldProps('owner')}
              label='PERSON’S NAME ON CARD'
              onChange={handleInputChange}
              error={getError('owner')}
              touched={!!touched.owner}
              onReset={resetFieldHandler('owner')}
              isFieldValue={!!values.owner}
              size='lg'
              containerStyles={{
                '& label': { fontSize: { base: '14px', lg: '15px' } }
              }}
              fontSize='21px'
            />
            <Flex
              flexWrap='wrap'
              gap='15px'
              justifyContent='space-between'
              flexDirection={{ base: 'row-reverse', lg: 'row' }}
            >
              <Flex
                gap='16px'
                width='100%'
                flexDirection={{
                  base: 'column',
                  sm: 'row'
                }}
                alignItems='flex-end'
              >
                <SelectField
                  name='exp_month'
                  setFieldValue={handleSelectChange}
                  setFieldTouched={setFieldTouched}
                  validateField={validateField}
                  value={values.exp_month}
                  options={months}
                  error={getApiError('exp_month', errorMsg as string)}
                  touched={!!touched.exp_month}
                  isSearchable
                  bordered
                  placeholder={isMobile ? 'Select month' : ''}
                  label='Expiration  Date'
                  stylesControl={{ height: '60px', fontSize: '21px' }}
                  stylesOption={{ fontSize: '21px' }}
                  formControlProps={{
                    sx: {
                      '& label': { fontSize: { base: '14px', lg: '15px' } }
                    }
                  }}
                />

                <SelectField
                  name='exp_year'
                  width={{ base: '100%', lg: '70%' }}
                  setFieldValue={handleSelectChange}
                  setFieldTouched={setFieldTouched}
                  validateField={validateField}
                  value={values.exp_year}
                  options={yearsOptions}
                  error={getApiError('exp_year', errorMsg as string)}
                  touched={!!touched.exp_year}
                  isSearchable
                  bordered
                  placeholder={isMobile ? 'Select year' : ''}
                  stylesControl={{ height: '60px', fontSize: '21px' }}
                  stylesOption={{ fontSize: '21px' }}
                  formControlProps={{
                    sx: {
                      '& label': { fontSize: { base: '14px', lg: '15px' } }
                    }
                  }}
                />
              </Flex>

              <Flex
                gap={{ base: '16px', sm: '29px' }}
                width='100%'
                alignItems='center'
                flexDirection={{ base: 'column', sm: 'row' }}
              >
                <TextField
                  {...getFieldProps('card_cvv2')}
                  label='Security Code'
                  error={getError('card_cvv2')}
                  onChange={handleInputChange}
                  touched={!!touched.card_cvv2}
                  onReset={resetFieldHandler('card_cvv2')}
                  isFieldValue={!!values.card_cvv2}
                  width={{ base: '100%', sm: '150px', md: '130px' }}
                  type='password'
                  mask={securityCodeMask}
                  size='lg'
                  containerStyles={{
                    '& label': { fontSize: { base: '14px', lg: '15px' } }
                  }}
                  fontSize='21px'
                />

                <Text
                  fontSize={{
                    base: '12px',
                    md: '15px',
                    lg: '12px',
                    xl: '14px'
                  }}
                  color={newCardForm.secureMsg}
                  as='span'
                  fontWeight='regular'
                  textTransform='uppercase'
                  lineHeight={['17px', '20px']}
                  flex={1}
                  pt={{ base: 0, sm: '20px' }}
                  textAlign={{ base: 'center', sm: 'left' }}
                >
                  Security Code is used for card verification and{' '}
                  <NoWrap>never stored</NoWrap>
                </Text>
              </Flex>
            </Flex>
          </Flex>

          <Flex
            w={{ base: '100%', lg: '50%' }}
            pl={{ base: '0', lg: '20px', xl: '30px' }}
            pt={{ base: '30px', lg: '0' }}
            gap='15px'
            flexWrap='wrap'
          >
            <TextField
              {...getFieldProps('addr_ln1')}
              label='Billing Address'
              onChange={handleInputChange}
              error={getError('addr_ln1')}
              touched={!!touched.addr_ln1}
              onReset={resetFieldHandler('addr_ln1')}
              isFieldValue={!!values.addr_ln1}
              w='100%'
              size='lg'
              containerStyles={{
                '& label': { fontSize: { base: '14px', lg: '15px' } }
              }}
              fontSize='21px'
            />

            <TextField
              {...getFieldProps('city')}
              label='City'
              onChange={handleInputChange}
              error={getError('city')}
              touched={!!touched.city}
              onReset={resetFieldHandler('city')}
              isFieldValue={!!values.city}
              w='100%'
              size='lg'
              containerStyles={{
                '& label': { fontSize: { base: '14px', lg: '15px' } }
              }}
              fontSize='21px'
            />

            <Flex
              gap='16px'
              width={{ base: '100%', md: 'calc(76.6% - 10px)', lg: '100%' }}
            >
              <Box w={{ base: '66%', sm: '100%' }}>
                <SelectField
                  name='state'
                  setFieldValue={handleSelectChange}
                  setFieldTouched={setFieldTouched}
                  validateField={validateField}
                  value={values.state}
                  options={formattedStates}
                  error={getError('state')}
                  touched={!!touched.state}
                  isSearchable
                  bordered
                  placeholder=''
                  label='State'
                  width='100%'
                  stylesControl={{ height: '60px', fontSize: '21px' }}
                  stylesOption={{ fontSize: '21px' }}
                  formControlProps={{
                    sx: {
                      '& label': { fontSize: { base: '14px', lg: '15px' } }
                    }
                  }}
                />
              </Box>

              <TextField
                {...getFieldProps('zip')}
                label='Zip Code'
                onChange={handleInputChange}
                error={getError('zip')}
                touched={!!touched.zip}
                onReset={resetFieldHandler('zip')}
                isFieldValue={!!values.zip}
                width={['40%', '70%']}
                mask={zipCodeMask}
                marginBottom='5px'
                size='lg'
                inputStyles={{
                  paddingRight: '20px'
                }}
                containerStyles={{
                  '& label': { fontSize: { base: '14px', lg: '15px' } }
                }}
                fontSize='21px'
              />
            </Flex>
          </Flex>

          {APIError && (
            <Text w='100%' textAlign='center' color='red.300' mt='20px'>
              {APIError as string}
            </Text>
          )}
        </Flex>
      </Stack>

      {enableOttFlow && !isGuestFlow && (
        <Flex
          mt={{ base: '30px', sm: '40px' }}
          mb={{ base: '-20px', sm: '-10px' }}
          justifyContent='center'
        >
          <Checkbox
            isChecked={shouldSaveCard}
            onChange={handleSaveCardChange}
            size='md'
          >
            <Text
              ml='4px'
              fontSize='14px'
              color='gray.600'
              textTransform='uppercase'
            >
              Save this credit card for future use
            </Text>
          </Checkbox>
        </Flex>
      )}

      <Flex
        gap={['20px', '30px']}
        marginX='auto'
        w='100%'
        direction={isGuestFlow ? 'row-reverse' : 'row'}
        justifyContent='center'
        flexWrap='wrap'
        alignItems='center'
        mt='50px'
      >
        <Button
          type='submit'
          colorScheme='primary'
          variant='solid'
          w='272px'
          h='45px'
          isDisabled={isSubmitDisabled}
        >
          {getButtonText()}
        </Button>

        <Button
          onClick={isAlternativeBehavior ? onBack : handleCancel}
          colorScheme='primary'
          variant='outline'
          w='272px'
          h='45px'
        >
          {isAlternativeBehavior ? 'Back' : 'Cancel'}
        </Button>
      </Flex>

      {(isLoading || isStatesLoading) && (
        <Loader fixed bg={newCardForm.loaderBg} />
      )}

      <ErrorModal
        isOpen={!!APIError}
        apiErrorText={APIError as string}
        onClose={handleClearError()}
        onPaymentAgain={handleSubmit}
        onAnotherCard={handleClearError(true)}
        title={title}
        description={description}
      />
    </Box>
  );
};

export default NewCardForm;
